import React, { useState, useEffect, useContext } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Button, TextField, Typography, Chip } from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { IconButton, InputAdornment, Switch } from "@mui/material";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import EmailIcon from "@material-ui/icons/Email";
import LockIcon from "@material-ui/icons/Lock";
import { Helmet } from "react-helmet";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    width: "100vw",
    height: "100vh",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },
  brandSide: {
    flex: 1,
    position: "relative",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(6),
    background: `radial-gradient(130% 150% at 10% 0%, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 46%, ${theme.palette.primary.dark} 100%)`,
    color: "#fff",
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  brandContent: {
    position: "relative",
    zIndex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    gap: theme.spacing(1),
  },
  brandGlow: {
    position: "absolute",
    inset: 0,
    backgroundImage:
      "radial-gradient(circle at 85% 18%, rgba(255,255,255,0.14) 0, transparent 38%), radial-gradient(circle at 15% 92%, rgba(255,255,255,0.10) 0, transparent 42%)",
  },
  brandLogoBadge: {
    position: "relative",
    zIndex: 1,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#fff",
    borderRadius: 24,
    padding: theme.spacing(3, 5),
    boxShadow: "0 16px 40px rgba(0,0,0,0.22)",
    marginBottom: theme.spacing(2),
  },
  brandLogo: {
    width: "min(85%, 520px)",
    height: "auto",
    display: "block",
  },
  brandHeadline: {
    position: "relative",
    zIndex: 1,
    fontWeight: 700,
    fontSize: "1.75rem",
    lineHeight: 1.3,
    whiteSpace: "nowrap",
  },
  brandSubtext: {
    position: "relative",
    zIndex: 1,
    fontSize: "1rem",
    opacity: 0.85,
    maxWidth: 380,
    marginTop: theme.spacing(1),
  },
  brandChips: {
    position: "relative",
    zIndex: 1,
    display: "flex",
    justifyContent: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  brandChip: {
    color: "#fff",
    borderColor: "rgba(255,255,255,0.35)",
    backgroundColor: "rgba(255,255,255,0.1)",
  },
  formSide: {
    flex: 1,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: theme.spacing(5),
    background: theme.palette.background.default,
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2.5),
    },
  },
  formContainer: {
    width: "100%",
    maxWidth: 380,
    animation: "$fadeIn 0.6s ease-in-out",
  },
  "@keyframes fadeIn": {
    "0%": { opacity: 0, transform: "translateY(16px)" },
    "100%": { opacity: 1, transform: "translateY(0)" },
  },
  formLogo: {
    display: "block",
    height: 120,
    width: "auto",
    margin: `0 auto ${theme.spacing(1.5)}px`,
  },
  formSubtitle: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(3),
  },
  field: {
    marginBottom: theme.spacing(1),
  },
  optionsRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    margin: theme.spacing(1, 0, 2),
  },
  rememberMe: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  forgotPasswordLink: {
    color: theme.palette.primary.main,
    textDecoration: "none",
    fontWeight: 500,
    fontSize: "0.8125rem",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  submitBtn: {
    borderRadius: 10,
    padding: theme.spacing(1.3),
    fontWeight: 700,
  },
  divider: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    margin: theme.spacing(2.5, 0),
    "&::before, &::after": {
      content: '""',
      flex: 1,
      height: 1,
      backgroundColor: theme.palette.divider,
    },
  },
  registerBtn: {
    borderRadius: 10,
    padding: theme.spacing(1.1),
    fontWeight: 600,
  },
  copyright: {
    textAlign: "center",
    marginTop: theme.spacing(3),
    color: theme.palette.text.disabled,
    fontSize: "0.6875rem",
  },
  whatsappButton: {
    position: "fixed",
    bottom: theme.spacing(2.5),
    right: theme.spacing(2.5),
    backgroundColor: "#fff",
    borderRadius: "50%",
    width: 44,
    height: 44,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    boxShadow: "0 4px 14px rgba(0,0,0,0.18)",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    cursor: "pointer",
    zIndex: 999,
    "&:hover": {
      transform: "scale(1.08)",
      boxShadow: "0 6px 18px rgba(0,0,0,0.22)",
    },
  },
  whatsappIcon: {
    width: 24,
    height: 24,
    objectFit: "contain",
  },
}));

const Login = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { handleLogin } = useContext(AuthContext);
  const [user, setUser] = useState({ email: "", password: "", remember: false });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [userCreationEnabled, setUserCreationEnabled] = useState(true);
  const backendUrl =
    process.env.REACT_APP_BACKEND_URL === "https://localhost:8090"
      ? "https://localhost:8090"
      : process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const fetchUserCreationStatus = async () => {
      try {
        const response = await fetch(`${backendUrl}/settings/userCreation`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });
        if (!response.ok) {
          throw new Error("Failed to fetch user creation status");
        }
        const data = await response.json();
        setUserCreationEnabled(data.userCreation === "enabled");
      } catch (err) {
        console.error("Erro ao verificar userCreation:", err);
        setUserCreationEnabled(false);
      }
    };
    fetchUserCreationStatus();
  }, [backendUrl]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post("/auth/login", user);
      handleLogin(response.data);
    } catch (err) {
      setError(err.response?.data?.message || "Erro ao fazer login");
    }
  };

  const logoSrc = theme.mode === "light" ? theme.calculatedLogoLight?.() : theme.calculatedLogoDark?.();
  const appName = theme.appName || "Sistema";

  return (
    <>
      <Helmet>
        <title>{appName}</title>
      </Helmet>
      <div className={classes.root}>
        <div className={classes.brandSide}>
          <div className={classes.brandGlow}></div>
          <div className={classes.brandLogoBadge}>
            <img src={logoSrc} alt={appName} className={classes.brandLogo} />
          </div>
          <div className={classes.brandContent}>
            <Typography className={classes.brandHeadline}>
              Todo atendimento, uma só tela.
            </Typography>
            <Typography className={classes.brandSubtext}>
              WhatsApp, IA e sua equipe organizados em um único lugar.
            </Typography>
            <div className={classes.brandChips}>
              <Chip size="small" variant="outlined" label="Multiatendimento" className={classes.brandChip} />
              <Chip size="small" variant="outlined" label="IA integrada" className={classes.brandChip} />
            </div>
          </div>
        </div>
        <div className={classes.formSide}>
          <form className={classes.formContainer} onSubmit={handleSubmit}>
            <img src={logoSrc} alt={appName} className={classes.formLogo} />
            <Typography variant="body2" className={classes.formSubtitle}>
              Entre com sua conta pra continuar
            </Typography>
            {error && (
              <Typography color="error" variant="body2" style={{ marginBottom: 8 }}>
                {error}
              </Typography>
            )}
            <TextField
              label="Email ou usuário"
              variant="outlined"
              fullWidth
              margin="dense"
              type="text"
              className={classes.field}
              value={user.email}
              onChange={(e) => setUser({ ...user, email: e.target.value })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon fontSize="small" color="disabled" />
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Senha"
              variant="outlined"
              fullWidth
              margin="dense"
              type={showPassword ? "text" : "password"}
              className={classes.field}
              value={user.password}
              onChange={(e) => setUser({ ...user, password: e.target.value })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon fontSize="small" color="disabled" />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <div className={classes.optionsRow}>
              <div className={classes.rememberMe}>
                <Switch
                  size="small"
                  checked={user.remember}
                  onChange={(e) => setUser({ ...user, remember: e.target.checked })}
                  name="remember"
                  sx={{
                    "& .Mui-checked": { color: theme.palette.primary.main },
                    "& .Mui-checked + .MuiSwitch-track": { backgroundColor: theme.palette.primary.main },
                  }}
                />
                <Typography variant="body2" color="textSecondary">Lembrar de mim</Typography>
              </div>
              <RouterLink to="/forgot-password" className={classes.forgotPasswordLink}>
                Esqueceu a senha?
              </RouterLink>
            </div>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              className={classes.submitBtn}
            >
              Entrar
            </Button>
            {userCreationEnabled && (
              <>
                <div className={classes.divider}>
                  <Typography variant="caption" color="textSecondary">ainda não tem conta</Typography>
                </div>
                <Button
                  component={RouterLink}
                  to="/signup"
                  variant="outlined"
                  color="primary"
                  fullWidth
                  className={classes.registerBtn}
                >
                  Cadastre-se
                </Button>
              </>
            )}
            <Typography className={classes.copyright}>
              © {new Date().getFullYear()} {appName} — uma empresa do Grupo RR Strike Soluções Comerciais
            </Typography>
          </form>
        </div>
        <div className={classes.whatsappButton} onClick={() => window.open("https://wa.me/551125425400")}>
          <img
            src="https://i.ibb.co/bRrKZbND/Official-Whatsapp.png"
            alt="WhatsApp"
            className={classes.whatsappIcon}
          />
        </div>
      </div>
    </>
  );
};

export default Login;
